import React, {Dispatch, SetStateAction, useMemo} from 'react';
import {Autoplay, EffectFade, Pagination} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import DesktopSliderItem from '@dazn/lp-web/src/components/BannerSlider/DesktopSlider/DesktopSliderItem';
import {IBannerSlide} from '@dazn/lp-web/src/types';
import {useVideoBannerContext} from '@dazn/lp-web/src/components/VideoBanner/VideoBannerContext';
import {
    checkIfSponsorshipLogoEnabled,
    checkIfSSAdsEnabledForLP,
    getLogoSlotPath
} from '@dazn/lp-web/src/features/checkIfSponsorshipLogoEnabled';
import {SponsorshipAds} from '@dazn/lp-web/src/components/SponsorShipAds/SponsorshipAds';
import {getPageId} from '@dazn/lp-web/src/utils/pageUtils';
import {bannerTypes} from '@dazn/lp-web/src/constants/bannerTypes.constants';

interface IBannerSliderProps {
    banners: IBannerSlide[];
    activeIndex: number;
    setActiveIndex: Dispatch<SetStateAction<number>>;
}

const DesktopSlider: React.FC<IBannerSliderProps> = ({
    banners,
    activeIndex,
    setActiveIndex
}) => {
    const swiperRef = React.useRef<any>(null);
    const {isPlayedOrSkiped, showModal, setIsPlayedOrSkiped} =
        useVideoBannerContext();
    const containsVideoBackground = banners?.some(
        (item) => item.backgroundType === 'video'
    );
    const shouldStopAutoplay =
        (!isPlayedOrSkiped || showModal) && containsVideoBackground;

    // Reset autoplay when video ends or when moving to the next slide
    React.useEffect(() => {
        if (swiperRef.current) {
            if (shouldStopAutoplay) {
                swiperRef.current.autoplay.stop(); // Stop Swiper's autoplay when video is playing
            } else {
                swiperRef.current.autoplay.start(); // Start Swiper's autoplay for regular slides
            }
        }
    }, [shouldStopAutoplay]);

    const nativeAppName: string = (window.dazn as ExtendedDAZN).native.appName;

    const shouldRenderSponsorshipLogo = () => {
        if (getPageId() === 'nfl' || window.location.href.includes('nfl')) {
            return true;
        }
        return false;
    };

    const checkIfSSAdsEnabledForLandingPages = () => {
        if (checkIfSponsorshipLogoEnabled() && shouldRenderSponsorshipLogo()) {
            const effectiveAppName =
                nativeAppName.trim() === '' ? 'web' : nativeAppName;
            const isLandingPagesEnabled =
                checkIfSSAdsEnabledForLP(effectiveAppName);
            return isLandingPagesEnabled;
        }
    };

    const handlePaginationClick = (index: number) => {
        if (index !== 0) {
            setIsPlayedOrSkiped(true);
        }
    };

    const isRotatingBannerExist = useMemo(() => {
        return banners?.find(
            (banner) =>
                banner?.bannerType?.toLowerCase() ===
                bannerTypes.ROTATING_CONTENT.toLowerCase()
        );
    }, [banners]);

    const heroBannerSliderClassName = isRotatingBannerExist
        ? 'heroBannerSlider heroBannerSliderRotatingBanner'
        : 'heroBannerSlider';

    return (
        <div className={heroBannerSliderClassName}>
            <Swiper
                initialSlide={0}
                modules={[Pagination, EffectFade, Autoplay]}
                slidesPerView={1}
                spaceBetween={30}
                pagination={{clickable: true}}
                effect="fade"
                ref={swiperRef}
                loop={banners?.length > 1}
                autoplay={{
                    delay: 20000,
                    disableOnInteraction: false
                }}
                onSlideChange={(swiper: any) => {
                    setActiveIndex(swiper?.realIndex ?? 0);
                }}
                speed={500}
                breakpoints={{768: {effect: 'fade'}}}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper; // Store Swiper instance
                    swiper.on('paginationUpdate', () =>
                        handlePaginationClick(swiper?.realIndex)
                    );
                }}
            >
                {banners?.map((banner: any, index: number) => (
                    <SwiperSlide key={index}>
                        <DesktopSliderItem data={banner} banners={banners} />
                    </SwiperSlide>
                ))}
            </Swiper>
            {banners.length > 0 && checkIfSSAdsEnabledForLandingPages() && (
                <SponsorshipAds
                    index={activeIndex}
                    slotPath={getLogoSlotPath()}
                />
            )}
        </div>
    );
};

export default DesktopSlider;
